/*
 * CardFanatics Cart / Variant Guard V13
 *
 * Global rules for every Soccer Club product-options component:
 *
 * - Reads the live Shopify cart (/cart.js).
 * - Marks every uniquely identifiable variant already in the cart with IN CART.
 * - Applies the same behavior on:
 *      * full product pages
 *      * /collections/breaks/products/... pages
 *      * Select Options / quick-edit popup
 *      * sticky/product option components
 *      * any other [data-js-product] using product-options
 * - If the selected managed variant has no remaining stock because the
 *   available quantity is already in the cart, Add to Cart is blocked.
 * - If stock remains, quantity is limited to the remaining amount.
 * - Collection cards that only OPEN Select Options are not evaluated against
 *   their default variant before the customer chooses an option.
 * - Keeps the V8 direct-add fix for Soccer Club's Select Options popup to avoid
 *   its preliminary quantity:0 /cart/add.js request.
 */
(function () {
    'use strict';

    if (window.CardFanaticsCartGuardV13) {
        return;
    }

    var cartQuantities = new Map();
    var cartRequest = null;
    var bypassButtons = new WeakSet();

    var quickEditMode = null;
    var popupPoll = null;
    var popupPollCount = 0;
    var popupObserver = null;
    var popupObserverStopTimer = null;
    var popupObserverDebounce = null;

    var MAX_POPUP_POLL = 35;

    var TEXT_ADD_DEFAULT = 'Add to cart';
    var TEXT_OPTION_IN_CART = 'Option already in cart';

    var NOTICE_OPTION_IN_CART =
        'This option is already in your cart. Please choose another option.';


    /* ==========================================================
       COMMON / CART
       ========================================================== */

    function shopifyRoot() {
        if (
            window.Shopify &&
            Shopify.routes &&
            typeof Shopify.routes.root === 'string'
        ) {
            return Shopify.routes.root;
        }

        return '/';
    }


    function toInt(value, fallback) {
        var parsed = parseInt(value, 10);

        if (Number.isFinite(parsed)) {
            return parsed;
        }

        return typeof fallback === 'number'
            ? fallback
            : 0;
    }


    function getProductRoot(element) {
        if (!element || !element.closest) {
            return null;
        }

        return element.closest('[data-js-product]');
    }


    function readProductJson(product) {
        if (!product) {
            return null;
        }

        var script = product.querySelector(
            'script.data-json-product[type="application/json"]'
        );

        if (!script) {
            return null;
        }

        try {
            return JSON.parse(script.textContent.trim());
        } catch (error) {
            console.warn(
                'CardFanatics: invalid product JSON.',
                error
            );

            return null;
        }
    }


    function updateCartMap(cart) {
        cartQuantities = new Map();

        if (
            !cart ||
            !Array.isArray(cart.items)
        ) {
            return;
        }

        cart.items.forEach(function (item) {
            var variantId = toInt(
                item.variant_id || item.id,
                0
            );

            if (!variantId) {
                return;
            }

            cartQuantities.set(
                variantId,
                (cartQuantities.get(variantId) || 0) +
                    toInt(item.quantity, 0)
            );
        });
    }


    function fetchCart(force) {
        if (cartRequest && !force) {
            return cartRequest;
        }

        cartRequest = fetch(
            shopifyRoot() + 'cart.js',
            {
                method: 'GET',
                credentials: 'same-origin',
                cache: 'no-store',
                headers: {
                    'Accept': 'application/json'
                }
            }
        )
        .then(function (response) {
            if (!response.ok) {
                throw new Error(
                    'Unable to read Shopify cart.'
                );
            }

            return response.json();
        })
        .then(function (cart) {
            updateCartMap(cart);
            return cart;
        })
        .finally(function () {
            cartRequest = null;
        });

        return cartRequest;
    }


    function quantityInCart(variantId) {
        return cartQuantities.get(variantId) || 0;
    }


    function variantIsInCart(variantId) {
        return (
            !!variantId &&
            quantityInCart(variantId) > 0
        );
    }


    /* ==========================================================
       PRODUCT / VARIANT
       ========================================================== */

    function getSelectedVariantId(product, explicitButton) {
        if (!product) {
            return null;
        }

        var id = toInt(
            product.getAttribute(
                'data-product-variant-id'
            ),
            0
        );

        if (id) {
            return id;
        }

        var variantInput = product.querySelector(
            '[data-js-product-variant-input]'
        );

        if (
            variantInput &&
            variantInput.value
        ) {
            id = toInt(
                variantInput.value,
                0
            );

            if (id) {
                return id;
            }
        }

        var selectedId = product.querySelector(
            'select[name="id"]'
        );

        if (
            selectedId &&
            selectedId.value
        ) {
            id = toInt(
                selectedId.value,
                0
            );

            if (id) {
                return id;
            }
        }

        var inputId = product.querySelector(
            'input[name="id"]'
        );

        if (
            inputId &&
            inputId.value
        ) {
            id = toInt(
                inputId.value,
                0
            );

            if (id) {
                return id;
            }
        }

        if (explicitButton) {
            id = toInt(
                explicitButton.getAttribute(
                    'data-pid'
                ),
                0
            );

            if (id) {
                return id;
            }
        }

        return null;
    }


    function findVariant(json, variantId) {
        if (
            !json ||
            !Array.isArray(json.variants) ||
            !variantId
        ) {
            return null;
        }

        for (
            var index = 0;
            index < json.variants.length;
            index++
        ) {
            var variant = json.variants[index];

            if (
                toInt(variant.id, 0) ===
                variantId
            ) {
                return variant;
            }
        }

        return null;
    }


    function inventoryQuantity(json, variantId) {
        if (
            !json ||
            !Array.isArray(
                json.variants_quantity
            )
        ) {
            return 0;
        }

        for (
            var index = 0;
            index <
                json.variants_quantity.length;
            index++
        ) {
            var item =
                json.variants_quantity[index];

            if (
                toInt(item.id, 0) ===
                variantId
            ) {
                return Math.max(
                    0,
                    toInt(item.quantity, 0)
                );
            }
        }

        /*
         * Soccer Club omits inventory_quantity == 0
         * from variants_quantity.
         */
        return 0;
    }


    function getStockInfo(product, variantId) {
        var json = readProductJson(product);
        var variant = findVariant(
            json,
            variantId
        );

        if (!variant) {
            return {
                known: false,
                managed: false,
                stock: null,
                variant: null
            };
        }

        if (
            !variant.inventory_management ||
            variant.inventory_policy ===
                'continue'
        ) {
            return {
                known: true,
                managed: false,
                stock: null,
                variant: variant
            };
        }

        return {
            known: true,
            managed: true,
            stock: inventoryQuantity(
                json,
                variantId
            ),
            variant: variant
        };
    }


    function hasOptionUI(product) {
        if (!product) {
            return false;
        }

        return !!product.querySelector(
            'product-options .product-options__value, ' +
            '.js-product-options .product-options__value'
        );
    }


    function isQuickEditProduct(product) {
        return !!(
            product &&
            product.closest(
                '#quick-editcart-modal'
            )
        );
    }


    function isSelectOptionsLauncherCard(product) {
        if (
            !product ||
            isQuickEditProduct(product)
        ) {
            return false;
        }

        /*
         * A collection/home card with Select Options should not
         * make a stock decision using its first/default variant.
         * If actual options are rendered on the same root, it is
         * no longer treated as launcher-only.
         */
        return (
            !!product.querySelector(
                '.js_select_options'
            ) &&
            !hasOptionUI(product)
        );
    }


    /* ==========================================================
       OPTION BADGES
       ========================================================== */

    function optionValue(control) {
        if (!control) {
            return null;
        }

        if (
            control.tagName === 'OPTION'
        ) {
            return control.value;
        }

        var input = control.querySelector(
            'input'
        );

        if (input) {
            return input.value;
        }

        return control.getAttribute(
            'data-value'
        );
    }


    function isOptionControlActive(control) {
        if (!control) {
            return false;
        }

        if (
            control.tagName === 'OPTION'
        ) {
            return !!control.selected;
        }

        if (
            control.classList.contains(
                'active'
            )
        ) {
            return true;
        }

        var input = control.querySelector(
            'input'
        );

        return !!(
            input &&
            input.checked
        );
    }


    function restoreOptionLabel(control) {
        if (
            !control ||
            control.tagName !== 'OPTION'
        ) {
            return;
        }

        var original =
            control.getAttribute(
                'data-cf-original-option-label'
            );

        if (original !== null) {
            control.textContent = original;
            control.removeAttribute(
                'data-cf-original-option-label'
            );
        }
    }


    function clearOptionBadges(product) {
        if (!product) {
            return;
        }

        product
            .querySelectorAll(
                '.product-options__value'
            )
            .forEach(function (control) {
                control.classList.remove(
                    'cf-option-in-cart-passive',
                    'cf-option-selected-in-cart'
                );

                control.removeAttribute(
                    'data-cf-variant-in-cart'
                );

                restoreOptionLabel(control);
            });
    }


    function decorateOptionAsInCart(
        control,
        variantId,
        selected
    ) {
        if (!control) {
            return;
        }

        control.setAttribute(
            'data-cf-variant-in-cart',
            String(variantId)
        );

        control.classList.add(
            selected
                ? 'cf-option-selected-in-cart'
                : 'cf-option-in-cart-passive'
        );

        if (
            control.tagName === 'OPTION'
        ) {
            if (
                !control.hasAttribute(
                    'data-cf-original-option-label'
                )
            ) {
                control.setAttribute(
                    'data-cf-original-option-label',
                    control.textContent
                );
            }

            var original =
                control.getAttribute(
                    'data-cf-original-option-label'
                );

            control.textContent =
                original + ' — IN CART';
        }
    }


    function markAllInCartOptions(product) {
        if (
            !product ||
            !hasOptionUI(product)
        ) {
            return;
        }

        clearOptionBadges(product);

        var json = readProductJson(product);

        if (
            !json ||
            !Array.isArray(json.variants)
        ) {
            return;
        }

        var selectedVariantId =
            getSelectedVariantId(product);

        var selectedIsInCart =
            variantIsInCart(
                selectedVariantId
            );

        product
            .querySelectorAll(
                'product-options [data-position], ' +
                '.js-product-options [data-position]'
            )
            .forEach(function (group) {
                var position =
                    toInt(
                        group.getAttribute(
                            'data-position'
                        ),
                        0
                    ) - 1;

                if (position < 0) {
                    return;
                }

                group
                    .querySelectorAll(
                        '.product-options__value'
                    )
                    .forEach(function (control) {
                        var value =
                            optionValue(control);

                        if (
                            value === null ||
                            value === undefined
                        ) {
                            return;
                        }

                        var matching =
                            json.variants.filter(
                                function (variant) {
                                    return (
                                        Array.isArray(
                                            variant.options
                                        ) &&
                                        String(
                                            variant.options[
                                                position
                                            ]
                                        ) ===
                                            String(value)
                                    );
                                }
                            );

                        /*
                         * Passive IN CART can only be attached to an
                         * option value when it maps to one exact variant.
                         * This is the normal Break setup (one option,
                         * one variant per team/player).
                         */
                        if (
                            matching.length === 1
                        ) {
                            var exactId = toInt(
                                matching[0].id,
                                0
                            );

                            if (
                                exactId &&
                                variantIsInCart(
                                    exactId
                                )
                            ) {
                                decorateOptionAsInCart(
                                    control,
                                    exactId,
                                    (
                                        selectedIsInCart &&
                                        exactId ===
                                            selectedVariantId &&
                                        isOptionControlActive(
                                            control
                                        )
                                    )
                                );
                            }

                            return;
                        }

                        /*
                         * For multi-dimensional products an individual
                         * value (e.g. "M") may map to many variants.
                         * Do not falsely label it passively. If the
                         * CURRENT exact combination is in the cart,
                         * however, its active controls are marked.
                         */
                        if (
                            selectedIsInCart &&
                            isOptionControlActive(
                                control
                            )
                        ) {
                            decorateOptionAsInCart(
                                control,
                                selectedVariantId,
                                true
                            );
                        }
                    });
            });
    }


    /* ==========================================================
       QUANTITY LIMIT
       ========================================================== */

    function getQuantityInput(
        product,
        button
    ) {
        if (button) {
            var form = button.closest(
                'form'
            );

            if (form) {
                var formInput =
                    form.querySelector(
                        '[name="quantity"]'
                    );

                if (formInput) {
                    return formInput;
                }
            }
        }

        if (!product) {
            return null;
        }

        return product.querySelector(
            '[name="quantity"]'
        );
    }


    function rememberOriginalMaximum(
        input
    ) {
        if (
            !input ||
            input.hasAttribute(
                'data-cf-original-max'
            )
        ) {
            return;
        }

        input.setAttribute(
            'data-cf-original-max',
            input.getAttribute('max') || ''
        );

        input.setAttribute(
            'data-cf-original-data-max',
            input.getAttribute(
                'data-max'
            ) || ''
        );
    }


    function setQuantityMaximum(
        input,
        maximum
    ) {
        if (!input) {
            return;
        }

        rememberOriginalMaximum(input);

        input.setAttribute(
            'max',
            String(maximum)
        );

        input.setAttribute(
            'data-max',
            String(maximum)
        );

        if (window.jQuery) {
            window
                .jQuery(input)
                .data('max', maximum);
        }
    }


    function restoreQuantityMaximum(
        input
    ) {
        if (
            !input ||
            !input.hasAttribute(
                'data-cf-original-max'
            )
        ) {
            return;
        }

        var oldMax =
            input.getAttribute(
                'data-cf-original-max'
            );

        var oldDataMax =
            input.getAttribute(
                'data-cf-original-data-max'
            );

        if (oldMax) {
            input.setAttribute(
                'max',
                oldMax
            );
        } else {
            input.removeAttribute('max');
        }

        if (oldDataMax) {
            input.setAttribute(
                'data-max',
                oldDataMax
            );

            if (window.jQuery) {
                window
                    .jQuery(input)
                    .data(
                        'max',
                        toInt(
                            oldDataMax,
                            oldDataMax
                        )
                    );
            }
        } else {
            input.removeAttribute(
                'data-max'
            );

            if (window.jQuery) {
                window
                    .jQuery(input)
                    .removeData('max');
            }
        }
    }


    function clampQuantity(
        input,
        maximum
    ) {
        if (
            !input ||
            maximum <= 0
        ) {
            return false;
        }

        var current = Math.max(
            1,
            toInt(input.value, 1)
        );

        if (current <= maximum) {
            return false;
        }

        input.value = String(maximum);

        input.dispatchEvent(
            new Event(
                'change',
                {
                    bubbles: true
                }
            )
        );

        return true;
    }


    /* ==========================================================
       BUTTON STATE
       ========================================================== */

    function buttonTextNode(button) {
        if (!button) {
            return null;
        }

        return (
            button.querySelector(
                '.txt_add_to_cart'
            ) ||
            button.querySelector(
                '.text'
            )
        );
    }


    function stableAddToCartText() {
        return (
            window.products &&
            window.products.add_to_cart
                ? window.products.add_to_cart
                : TEXT_ADD_DEFAULT
        );
    }


    function forceStableAddToCartLabel(
        button
    ) {
        if (!button) {
            return;
        }

        var text =
            buttonTextNode(button);

        if (!text) {
            return;
        }

        /*
         * DISPLAY-TEXT RULE:
         *
         * The cart state is communicated by:
         * - IN CART option badges
         * - the explanatory cart notice
         * - the enabled/disabled CTA state
         *
         * The CTA itself always keeps the normal "Add to cart" label.
         * Never show "Added" or "Option already in cart" here.
         */
        var current =
            text.textContent
                .trim()
                .toLowerCase();

        if (
            current === 'added' ||
            current ===
                TEXT_OPTION_IN_CART
                    .toLowerCase() ||
            button.getAttribute(
                'data-cf-option-cart-blocked'
            ) === 'true'
        ) {
            text.textContent =
                stableAddToCartText();
        }
    }


    function clearTransientAddedState(
        button
    ) {
        if (!button) {
            return;
        }

        button.classList.remove(
            'added'
        );

        if (
            button.getAttribute(
                'data-button-status'
            ) === 'added'
        ) {
            button.removeAttribute(
                'data-button-status'
            );
        }
    }


    function rememberButtonText(button) {
        if (
            !button ||
            button.hasAttribute(
                'data-cf-original-button-text'
            )
        ) {
            return;
        }

        var text =
            buttonTextNode(button);

        if (!text) {
            return;
        }

        var value =
            text.textContent.trim();

        var normalized =
            value.toLowerCase();

        if (
            normalized === 'added' ||
            normalized ===
                TEXT_OPTION_IN_CART
                    .toLowerCase()
        ) {
            value =
                stableAddToCartText();
        }

        button.setAttribute(
            'data-cf-original-button-text',
            value
        );
    }


    function blockButtonForCart(
        button
    ) {
        if (!button) {
            return;
        }

        clearTransientAddedState(
            button
        );

        rememberButtonText(button);

        /*
         * Block the CTA logically without changing its visible text.
         * IN CART badges and the notice already explain the cart state.
         */
        forceStableAddToCartLabel(
            button
        );

        button.setAttribute(
            'data-cf-option-cart-blocked',
            'true'
        );

        button.setAttribute(
            'aria-disabled',
            'true'
        );

        button.setAttribute(
            'disabled',
            'disabled'
        );

        button.classList.add(
            'cf-option-cart-blocked'
        );

        forceStableAddToCartLabel(
            button
        );
    }


    function unblockButtonForCart(
        button
    ) {
        if (!button) {
            return;
        }

        var text =
            buttonTextNode(button);

        var currentText =
            text
                ? text.textContent.trim()
                : '';

        var currentNormalized =
            currentText.toLowerCase();

        var currentTextIsUsable =
            currentText !== '' &&
            currentNormalized !== 'added' &&
            currentNormalized !==
                TEXT_OPTION_IN_CART
                    .toLowerCase();

        clearTransientAddedState(
            button
        );

        if (
            button.getAttribute(
                'data-cf-option-cart-blocked'
            ) !== 'true'
        ) {
            return;
        }

        var original =
            button.getAttribute(
                'data-cf-original-button-text'
            );

        if (original) {
            var originalNormalized =
                original
                    .trim()
                    .toLowerCase();

            if (
                originalNormalized === 'added' ||
                originalNormalized ===
                    TEXT_OPTION_IN_CART
                        .toLowerCase()
            ) {
                original = '';
            }
        }

        if (text) {
            text.textContent =
                currentTextIsUsable
                    ? currentText
                    : (
                        original ||
                        stableAddToCartText()
                    );
        }

        button.removeAttribute(
            'data-cf-option-cart-blocked'
        );

        forceStableAddToCartLabel(
            button
        );

        button.removeAttribute(
            'data-cf-original-button-text'
        );

        button.removeAttribute(
            'aria-disabled'
        );

        button.classList.remove(
            'cf-option-cart-blocked'
        );

        /*
         * Don't revive a genuine theme Sold Out button.
         */
        if (
            button.getAttribute(
                'data-button-status'
            ) !== 'sold-out' &&
            !button.classList.contains(
                'sold_out'
            )
        ) {
            button.removeAttribute(
                'disabled'
            );

            button.style.pointerEvents =
                'auto';
        }
    }


    function getRelevantActionButtons(
        product
    ) {
        if (!product) {
            return [];
        }

        if (isQuickEditProduct(product)) {
            /*
             * The hidden .js_add_to_cart_button is part of Soccer
             * Club's internal quick-edit flow. Never block it.
             */
            var visible =
                product.querySelector(
                    '.js_edit_cart_button'
                );

            return visible
                ? [visible]
                : [];
        }

        return Array.prototype.slice.call(
            product.querySelectorAll(
                '.js_add_to_cart_button'
            )
        ).filter(function (button) {
            return !button.classList.contains(
                'js_select_options'
            );
        });
    }


    /* ==========================================================
       NOTICE
       ========================================================== */

    function removeCartNotice(product) {
        if (!product) {
            return;
        }

        product
            .querySelectorAll(
                '.cf-option-cart-notice'
            )
            .forEach(function (notice) {
                notice.remove();
            });
    }


    function getNoticeHost(product) {
        if (!product) {
            return null;
        }

        var options =
            product.querySelector(
                'product-options, ' +
                '.js-product-options'
            );

        if (
            options &&
            options.parentElement
        ) {
            return options.parentElement;
        }

        return product;
    }


    function showCartNotice(
        product,
        message,
        blocked
    ) {
        if (!product) {
            return;
        }

        removeCartNotice(product);

        if (!message) {
            return;
        }

        var host =
            getNoticeHost(product);

        if (!host) {
            return;
        }

        var notice =
            document.createElement(
                'div'
            );

        notice.className =
            'cf-option-cart-notice ' +
            (
                blocked
                    ? 'is-blocked'
                    : 'is-info'
            );

        notice.setAttribute(
            'role',
            'status'
        );

        notice.setAttribute(
            'aria-live',
            'polite'
        );

        notice.innerHTML =
            '<span class="' +
            'cf-option-cart-notice__icon' +
            '" aria-hidden="true">!</span>' +
            '<span class="' +
            'cf-option-cart-notice__text' +
            '"></span>';

        var text =
            notice.querySelector(
                '.cf-option-cart-notice__text'
            );

        text.textContent = message;

        host.appendChild(notice);
    }


    /* ==========================================================
       APPLY GLOBAL STATE
       ========================================================== */

    function clearLauncherCardState(
        product
    ) {
        if (!product) {
            return;
        }

        clearOptionBadges(product);
        removeCartNotice(product);

        var input =
            getQuantityInput(product);

        restoreQuantityMaximum(input);

        getRelevantActionButtons(product)
            .forEach(function (button) {
                unblockButtonForCart(button);
            });
    }


    function applyProductState(
        product,
        options
    ) {
        options = options || {};

        if (!product) {
            return null;
        }

        if (
            isSelectOptionsLauncherCard(
                product
            )
        ) {
            clearLauncherCardState(product);

            return {
                launcherOnly: true
            };
        }

        var variantId =
            getSelectedVariantId(
                product,
                options.button
            );

        markAllInCartOptions(product);

        if (!variantId) {
            return null;
        }

        var cartQty =
            quantityInCart(variantId);

        var stock =
            getStockInfo(
                product,
                variantId
            );

        var buttons =
            getRelevantActionButtons(
                product
            );

        var input =
            getQuantityInput(
                product,
                options.button ||
                    buttons[0]
            );

        if (!stock.managed) {
            restoreQuantityMaximum(input);

            buttons.forEach(function (button) {
                unblockButtonForCart(button);
            });

            if (cartQty > 0) {
                showCartNotice(
                    product,
                    'You have ' +
                        cartQty +
                        ' of this option in your cart.',
                    false
                );
            } else {
                removeCartNotice(product);
            }

            return {
                variantId: variantId,
                cartQuantity: cartQty,
                managed: false,
                blocked: false,
                remaining: null
            };
        }

        var remaining =
            Math.max(
                0,
                stock.stock - cartQty
            );

        /*
         * Stock 0 with nothing in the cart is a native Sold Out
         * situation. Leave the theme's own sold-out state alone.
         */
        if (
            stock.stock <= 0 &&
            cartQty <= 0
        ) {
            restoreQuantityMaximum(input);
            removeCartNotice(product);

            return {
                variantId: variantId,
                cartQuantity: 0,
                managed: true,
                stock: stock.stock,
                remaining: 0,
                nativeSoldOut: true,
                blocked: false
            };
        }

        if (remaining > 0) {
            setQuantityMaximum(
                input,
                remaining
            );

            var adjusted =
                clampQuantity(
                    input,
                    remaining
                );

            buttons.forEach(function (button) {
                unblockButtonForCart(button);
            });

            if (cartQty > 0) {
                showCartNotice(
                    product,
                    adjusted
                        ? (
                            'Quantity adjusted to ' +
                            remaining +
                            '. You already have ' +
                            cartQty +
                            ' in your cart.'
                        )
                        : (
                            'You have ' +
                            cartQty +
                            ' in your cart. You can add ' +
                            remaining +
                            ' more.'
                        ),
                    false
                );
            } else {
                removeCartNotice(product);
            }

            return {
                variantId: variantId,
                cartQuantity: cartQty,
                managed: true,
                stock: stock.stock,
                remaining: remaining,
                blocked: false
            };
        }

        /*
         * remaining == 0 because the available stock is already
         * represented in the cart.
         */
        buttons.forEach(function (button) {
            blockButtonForCart(button);
        });

        if (hasOptionUI(product)) {
            showCartNotice(
                product,
                NOTICE_OPTION_IN_CART,
                true
            );
        } else {
            showCartNotice(
                product,
                'You already have all available stock in your cart.',
                true
            );
        }

        return {
            variantId: variantId,
            cartQuantity: cartQty,
            managed: true,
            stock: stock.stock,
            remaining: 0,
            blocked: true
        };
    }


    function applyAllProductStates() {
        document
            .querySelectorAll(
                '[data-js-product]'
            )
            .forEach(function (product) {
                applyProductState(product);
            });
    }


    function refreshAll(delay) {
        window.setTimeout(
            function () {
                fetchCart(true)
                    .then(function () {
                        applyAllProductStates();
                    })
                    .catch(function (error) {
                        console.warn(
                            'CardFanatics cart refresh:',
                            error
                        );
                    });
            },
            typeof delay === 'number'
                ? delay
                : 0
        );
    }


    /* ==========================================================
       NORMAL ADD TO CART - SAFE QUANTITY
       ========================================================== */

    document.addEventListener(
        'click',
        function (event) {
            var button =
                event.target.closest(
                    '.js_add_to_cart_button'
                );

            if (!button) {
                return;
            }

            /*
             * Never intercept Soccer Club's hidden quick-edit
             * add button. V8/V11 handles the visible edit button.
             */
            if (
                button.closest(
                    '#quick-editcart-modal'
                )
            ) {
                return;
            }

            var product =
                getProductRoot(button);

            if (
                !product ||
                isSelectOptionsLauncherCard(
                    product
                )
            ) {
                return;
            }

            if (
                bypassButtons.has(button)
            ) {
                bypassButtons.delete(button);
                return;
            }

            var variantId =
                getSelectedVariantId(
                    product,
                    button
                );

            var stock =
                getStockInfo(
                    product,
                    variantId
                );

            if (!stock.managed) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();

            if (
                typeof event
                    .stopImmediatePropagation ===
                'function'
            ) {
                event.stopImmediatePropagation();
            }

            button.classList.add(
                'cf-stock-checking'
            );

            fetchCart(true)
                .then(function () {
                    var result =
                        applyProductState(
                            product,
                            {
                                button: button
                            }
                        );

                    button.classList.remove(
                        'cf-stock-checking'
                    );

                    if (
                        !result ||
                        result.blocked ||
                        result.nativeSoldOut
                    ) {
                        return;
                    }

                    bypassButtons.add(button);
                    button.click();
                })
                .catch(function (error) {
                    button.classList.remove(
                        'cf-stock-checking'
                    );

                    console.warn(
                        'CardFanatics pre-add cart check failed; using Shopify fallback.',
                        error
                    );

                    bypassButtons.add(button);
                    button.click();
                });
        },
        true
    );


    /* ==========================================================
       OPTIONS CHANGED - ALL LOCATIONS
       ========================================================== */

    document.body.addEventListener(
        'afterVariantUpdated',
        function () {
            /*
             * product-options.js has already updated each relevant
             * [data-js-product] root. Evaluate all roots so full
             * product, sticky ATC, quick edit, etc. stay in sync.
             */
            window.setTimeout(
                function () {
                    applyAllProductStates();
                },
                55
            );
        }
    );


    document.addEventListener(
        'click',
        function (event) {
            var option =
                event.target.closest(
                    '[data-js-product] ' +
                    '.product-options__value'
                );

            if (!option) {
                return;
            }

            var product =
                getProductRoot(option);

            window.setTimeout(
                function () {
                    if (product) {
                        applyProductState(product);
                    }
                },
                140
            );
        }
    );


    document.addEventListener(
        'change',
        function (event) {
            if (
                !event.target.matches(
                    '[data-js-product] ' +
                    '[data-js-option-select], ' +
                    '[data-js-product] ' +
                    'select.product-options__section'
                )
            ) {
                return;
            }

            var product =
                getProductRoot(
                    event.target
                );

            window.setTimeout(
                function () {
                    if (product) {
                        applyProductState(product);
                    }
                },
                100
            );
        }
    );


    /* ==========================================================
       SELECT OPTIONS / QUICK EDIT
       ========================================================== */

    function getQuickEditProduct() {
        return document.querySelector(
            '#quick-editcart-modal ' +
            '[data-js-product]'
        );
    }


    function getQuickEditVisibleButton(
        product
    ) {
        return product
            ? product.querySelector(
                '.js_edit_cart_button'
            )
            : null;
    }


    function stopPopupPolling() {
        if (popupPoll) {
            window.clearInterval(popupPoll);
            popupPoll = null;
        }

        popupPollCount = 0;
    }


    function startPopupPolling() {
        stopPopupPolling();

        popupPoll = window.setInterval(
            function () {
                popupPollCount++;

                var product =
                    getQuickEditProduct();

                if (product) {
                    stopPopupPolling();

                    fetchCart(true)
                        .then(function () {
                            applyProductState(product);
                        });

                    return;
                }

                if (
                    popupPollCount >=
                    MAX_POPUP_POLL
                ) {
                    stopPopupPolling();
                }
            },
            100
        );
    }


    function stopPopupObserver() {
        if (popupObserver) {
            popupObserver.disconnect();
            popupObserver = null;
        }

        if (popupObserverStopTimer) {
            window.clearTimeout(
                popupObserverStopTimer
            );

            popupObserverStopTimer = null;
        }

        if (popupObserverDebounce) {
            window.clearTimeout(
                popupObserverDebounce
            );

            popupObserverDebounce = null;
        }
    }


    function startPopupObserver() {
        stopPopupObserver();

        var modal =
            document.querySelector(
                '#quick-editcart-modal'
            );

        if (!modal) {
            return;
        }

        popupObserver =
            new MutationObserver(
                function () {
                    window.clearTimeout(
                        popupObserverDebounce
                    );

                    popupObserverDebounce =
                        window.setTimeout(
                            function () {
                                var product =
                                    getQuickEditProduct();

                                if (!product) {
                                    return;
                                }

                                fetchCart(true)
                                    .then(function () {
                                        applyProductState(
                                            product
                                        );
                                    });
                            },
                            35
                        );
                }
            );

        popupObserver.observe(
            modal,
            {
                childList: true,
                subtree: true
            }
        );

        popupObserverStopTimer =
            window.setTimeout(
                stopPopupObserver,
                2000
            );
    }


    function refreshQuickEditOnOpen() {
        /*
         * Refresh before and after Soccer Club injects the
         * quick-edit HTML so all IN CART badges are current.
         */
        fetchCart(true)
            .catch(function () {});

        startPopupObserver();
        startPopupPolling();

        [
            180,
            450,
            850,
            1300
        ].forEach(function (delay) {
            window.setTimeout(
                function () {
                    var product =
                        getQuickEditProduct();

                    if (!product) {
                        return;
                    }

                    fetchCart(true)
                        .then(function () {
                            applyProductState(
                                product
                            );
                        });
                },
                delay
            );
        });
    }


    document.addEventListener(
        'click',
        function (event) {
            var launcher =
                event.target.closest(
                    '.js_select_options, ' +
                    '#js_cart_popup .edit-cart'
                );

            if (!launcher) {
                return;
            }

            quickEditMode =
                launcher.classList.contains(
                    'js_select_options'
                )
                    ? 'add'
                    : 'edit';

            document.documentElement
                .setAttribute(
                    'data-cf-quick-edit-mode',
                    quickEditMode
                );

            var sourceProduct =
                getProductRoot(launcher);

            if (sourceProduct) {
                clearLauncherCardState(
                    sourceProduct
                );
            }

            refreshQuickEditOnOpen();
        },
        true
    );


    /* ==========================================================
       V8/V11 DIRECT SELECT OPTIONS ADD
       ========================================================== */

    function popupRequestedQuantity(
        product
    ) {
        var input =
            getQuantityInput(product);

        if (!input) {
            return 1;
        }

        var quantity = Math.max(
            1,
            toInt(input.value, 1)
        );

        var variantId =
            getSelectedVariantId(product);

        var stock =
            getStockInfo(
                product,
                variantId
            );

        if (stock.managed) {
            var remaining =
                Math.max(
                    0,
                    stock.stock -
                        quantityInCart(
                            variantId
                        )
                );

            if (
                remaining > 0 &&
                quantity > remaining
            ) {
                quantity = remaining;
                input.value = String(quantity);
            }
        }

        return quantity;
    }


    function setPopupAddingState(
        button,
        adding
    ) {
        if (!button) {
            return;
        }

        if (adding) {
            button.classList.add(
                'ajax_loading',
                'cf-stock-checking'
            );

            button.setAttribute(
                'aria-busy',
                'true'
            );
        } else {
            button.classList.remove(
                'ajax_loading',
                'cf-stock-checking'
            );

            button.removeAttribute(
                'aria-busy'
            );
        }
    }


    function openMiniCartAfterDirectAdd() {
        window.setTimeout(
            function () {
                if (!window.jQuery) {
                    return;
                }

                var $ = window.jQuery;

                if (
                    $.magnificPopup &&
                    typeof $.magnificPopup.close ===
                        'function'
                ) {
                    $.magnificPopup.close();
                }

                var drawerButton =
                    $(
                        '.push_side' +
                        '[data-id="#js_cart_popup"]'
                    );

                if (drawerButton.length) {
                    drawerButton
                        .first()
                        .trigger('click');
                }
            },
            50
        );
    }


    function directAddSelectedOption(
        product,
        button
    ) {
        var variantId =
            getSelectedVariantId(
                product,
                button
            );

        if (!variantId) {
            console.error(
                'CardFanatics: selected variant ID is missing.'
            );

            return Promise.resolve(false);
        }

        setPopupAddingState(
            button,
            true
        );

        return fetchCart(true)
            .then(function () {
                var state =
                    applyProductState(
                        product,
                        {
                            button: button
                        }
                    );

                if (
                    !state ||
                    state.blocked ||
                    state.nativeSoldOut
                ) {
                    return {
                        blocked: true
                    };
                }

                var quantity =
                    popupRequestedQuantity(
                        product
                    );

                var payload = {
                    items: [
                        {
                            id: variantId,
                            quantity: quantity
                        }
                    ],
                    sections: 'minicart',
                    sections_url:
                        window.location.pathname +
                        window.location.search
                };

                return fetch(
                    shopifyRoot() +
                        'cart/add.js',
                    {
                        method: 'POST',
                        credentials:
                            'same-origin',
                        headers: {
                            'Content-Type':
                                'application/json',
                            'Accept':
                                'application/json',
                            'X-Requested-With':
                                'XMLHttpRequest'
                        },
                        body:
                            JSON.stringify(
                                payload
                            )
                    }
                )
                .then(function (response) {
                    return response
                        .json()
                        .catch(function () {
                            return {};
                        })
                        .then(function (data) {
                            if (
                                !response.ok ||
                                data.status ||
                                data.errors
                            ) {
                                var message =
                                    data.description ||
                                    data.message ||
                                    (
                                        'Shopify rejected this option. HTTP ' +
                                        response.status
                                    );

                                console.error(
                                    'CardFanatics direct option add failed:',
                                    {
                                        status:
                                            response.status,
                                        variantId:
                                            variantId,
                                        quantity:
                                            quantity,
                                        response:
                                            data
                                    }
                                );

                                throw new Error(
                                    message
                                );
                            }

                            return data;
                        });
                });
            })
            .then(function (response) {
                if (
                    response &&
                    response.blocked
                ) {
                    return false;
                }

                document.body.dispatchEvent(
                    new CustomEvent(
                        'ajax:addToCart',
                        {
                            detail: {
                                form: null,
                                response: response
                            }
                        }
                    )
                );

                openMiniCartAfterDirectAdd();

                return true;
            })
            .catch(function (error) {
                console.error(
                    'CardFanatics Select Options add:',
                    error
                );

                showCartNotice(
                    product,
                    (
                        error &&
                        error.message
                            ? error.message
                            : 'Unable to add this option to the cart.'
                    ),
                    true
                );

                return false;
            })
            .finally(function () {
                setPopupAddingState(
                    button,
                    false
                );

                window.setTimeout(
                    function () {
                        fetchCart(true)
                            .then(function () {
                                applyAllProductStates();
                            });
                    },
                    120
                );
            });
    }


    /*
     * Capture the visible quick-edit Add to Cart only for
     * collection/home Select Options mode.
     *
     * Existing-line EDIT mode remains native Soccer Club behavior.
     */
    document.addEventListener(
        'click',
        function (event) {
            var button =
                event.target.closest(
                    '#quick-editcart-modal ' +
                    '.js_edit_cart_button'
                );

            if (
                !button ||
                quickEditMode !== 'add'
            ) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();

            if (
                typeof event
                    .stopImmediatePropagation ===
                'function'
            ) {
                event.stopImmediatePropagation();
            }

            var product =
                getQuickEditProduct();

            if (!product) {
                return;
            }

            fetchCart(true)
                .then(function () {
                    var state =
                        applyProductState(
                            product,
                            {
                                button: button
                            }
                        );

                    if (
                        state &&
                        !state.blocked &&
                        !state.nativeSoldOut
                    ) {
                        directAddSelectedOption(
                            product,
                            button
                        );
                    }
                });
        },
        true
    );


    /*
     * Even if another theme script removes disabled globally after
     * an AJAX add, don't let an exhausted selected variant through.
     */
    document.addEventListener(
        'click',
        function (event) {
            var button =
                event.target.closest(
                    '[data-cf-option-cart-blocked="true"]'
                );

            if (!button) {
                return;
            }

            event.preventDefault();
            event.stopPropagation();

            if (
                typeof event
                    .stopImmediatePropagation ===
                'function'
            ) {
                event.stopImmediatePropagation();
            }

            var product =
                getProductRoot(button);

            if (product) {
                applyProductState(product);
            }
        },
        true
    );


    function resetTransientAddedButtons() {
        document
            .querySelectorAll(
                '.js_add_to_cart_button.added, ' +
                '[data-js-product-button-add-to-cart].added'
            )
            .forEach(function (button) {
                var text =
                    buttonTextNode(button);

                clearTransientAddedState(
                    button
                );

                if (text) {
                    var current =
                        text.textContent
                            .trim()
                            .toLowerCase();

                    if (
                        current === 'added' ||
                        current ===
                            TEXT_OPTION_IN_CART
                                .toLowerCase()
                    ) {
                        text.textContent =
                            stableAddToCartText();
                    }
                }

                forceStableAddToCartLabel(
                    button
                );
            });
    }


    /* ==========================================================
       CART / DYNAMIC CONTENT EVENTS
       ========================================================== */

    [
        'ajax:addToCart',
        'ajax:updateCart',
        'ajax:deleteCart'
    ].forEach(function (eventName) {
        document.body.addEventListener(
            eventName,
            function () {
                refreshAll(110);
            }
        );
    });


    document.addEventListener(
        'drawer:Minicart',
        function () {
            resetTransientAddedButtons();
            refreshAll(0);
            refreshAll(100);
        }
    );


    document.addEventListener(
        'cardfanatics:minicart-opened',
        function () {
            resetTransientAddedButtons();
            refreshAll(0);
            refreshAll(100);
        }
    );


    document.addEventListener(
        'contentUpdated',
        function () {
            refreshAll(80);
        }
    );


    document.addEventListener(
        'shopify:section:load',
        function () {
            refreshAll(50);
        }
    );


    window.addEventListener(
        'pageshow',
        function () {
            refreshAll(0);
        }
    );


    window.CardFanaticsCartDebug =
        function () {
            var product =
                document.querySelector(
                    '#product-single[data-js-product], ' +
                    '#product-single [data-js-product]'
                ) ||
                document.querySelector(
                    '[data-js-product]'
                );

            var button =
                product
                    ? product.querySelector(
                        '[data-js-product-button-add-to-cart], ' +
                        '.js_add_to_cart_button'
                    )
                    : null;

            var variantInput =
                product
                    ? product.querySelector(
                        '[name="id"]'
                    )
                    : null;

            var text =
                buttonTextNode(button);

            return {
                guardVersion: 'V13',
                selectedVariantId:
                    variantInput
                        ? variantInput.value
                        : null,
                buttonVariantId:
                    button
                        ? button.getAttribute(
                            'data-pid'
                        )
                        : null,
                selectionToken:
                    button
                        ? button.getAttribute(
                            'data-cf-selection-token'
                        )
                        : null,
                buttonText:
                    text
                        ? text.textContent.trim()
                        : null,
                buttonClass:
                    button
                        ? button.className
                        : null,
                buttonDisabled:
                    button
                        ? button.disabled
                        : null,
                buttonStatus:
                    button
                        ? button.getAttribute(
                            'data-button-status'
                        )
                        : null,
                cartBlocked:
                    button
                        ? button.getAttribute(
                            'data-cf-option-cart-blocked'
                        )
                        : null,
                cartQuantities:
                    Array.from(
                        cartQuantities.entries()
                    )
            };
        };


    if (
        document.readyState ===
        'loading'
    ) {
        document.addEventListener(
            'DOMContentLoaded',
            function () {
                refreshAll(0);
            }
        );
    } else {
        refreshAll(0);
    }


    window.CardFanaticsCartGuardV13 = {
        refresh: function () {
            return fetchCart(true)
                .then(function () {
                    applyAllProductStates();
                });
        },

        refreshOptions:
            applyAllProductStates,

        refreshQuickEdit:
            refreshQuickEditOnOpen
    };


    /*
     * Compatibility alias for previous external references.
     * The asset filename stays exactly the same.
     */
    window.CardFanaticsCartGuardV12 =
        window.CardFanaticsCartGuardV13;

})();